/**
 * CameraCapture — In-app camera modal with live preview (Prompt 6.1 iter81.2, iter94 flash/zoom).
 *
 * Uses navigator.mediaDevices.getUserMedia (WebRTC) so the actual device
 * camera opens (not the gallery). Rear camera preferred (`environment`).
 *
 * iter94 eklenti:
 *   - Flaş (torch) toggle: MediaStreamTrack.applyConstraints({ advanced: [{ torch: true }] })
 *     (yalnız cihaz destekliyorsa; Android Chrome/Firefox rear cam'de tipik).
 *   - Zoom slider: track.getCapabilities().zoom aralığında uygulanır.
 *
 * Props:
 *   open        boolean
 *   onClose     () => void
 *   onCapture   (File) => void   — user shot a photo, receive it as File(jpg)
 *   title       string
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Modal, Btn } from "@/components/UI";
import { Camera, RefreshCw, Loader2, AlertTriangle, X, Zap, ZapOff } from "lucide-react";

const jpegType = "image/jpeg";

export default function CameraCapture({ open, onClose, onCapture, title = "Fotoğraf Çek" }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState("");
  const [facing, setFacing] = useState("environment");
  const [captured, setCaptured] = useState(null); // Blob URL after capture
  // iter94 flash & zoom
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [zoomCap, setZoomCap] = useState(null); // { min, max, step } or null
  const [zoom, setZoom] = useState(1);

  const stopStream = useCallback(() => {
    try {
      streamRef.current?.getTracks?.().forEach(t => t.stop());
    } catch { /* noop */ }
    streamRef.current = null;
  }, []);

  const _detectCapabilities = useCallback((stream) => {
    setTorchSupported(false);
    setZoomCap(null);
    const track = stream?.getVideoTracks?.()[0];
    if (!track || !track.getCapabilities) return;
    let caps = {};
    try { caps = track.getCapabilities(); } catch { caps = {}; }
    if (caps.torch === true || caps.torch === false || "torch" in caps) {
      // Chrome reports torch:true when supported
      setTorchSupported(!!caps.torch);
    }
    if (caps.zoom && typeof caps.zoom === "object") {
      setZoomCap({
        min: caps.zoom.min ?? 1,
        max: caps.zoom.max ?? 1,
        step: caps.zoom.step ?? 0.1,
      });
      // Reset zoom to current or min when a new stream starts
      let settings = {};
      try { settings = track.getSettings?.() || {}; } catch { settings = {}; }
      setZoom(settings.zoom || caps.zoom.min || 1);
    }
  }, []);

  const startStream = useCallback(async (mode) => {
    setErr("");
    setReady(false);
    setTorchOn(false);
    stopStream();
    if (!navigator.mediaDevices?.getUserMedia) {
      const secure = window.isSecureContext;
      setErr(secure
        ? "Bu cihaz/tarayıcı kamera erişimini desteklemiyor."
        : "Kamera yalnız HTTPS (güvenli bağlantı) üzerinden çalışır. Uygulamayı https:// ile açtığınızdan emin olun.");
      return;
    }
    try {
      const constraints = {
        audio: false,
        video: {
          facingMode: { ideal: mode },
          width:  { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try { await videoRef.current.play(); } catch { /* autoplay guard */ }
      }
      _detectCapabilities(stream);
      setReady(true);
    } catch (e) {
      const msg = e?.name === "NotAllowedError"
        ? "Kamera izni reddedildi. Tarayıcı ayarlarından izin vermelisiniz."
        : e?.name === "NotFoundError"
          ? "Bu cihazda kamera bulunamadı."
          : `Kamera açılamadı: ${e?.message || e}`;
      setErr(msg);
    }
  }, [stopStream, _detectCapabilities]);

  useEffect(() => {
    if (open) {
      startStream(facing);
    } else {
      stopStream();
      setCaptured(null);
      setReady(false);
      setTorchOn(false);
      setZoomCap(null);
    }
    return () => stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const switchCamera = () => {
    const next = facing === "environment" ? "user" : "environment";
    setFacing(next);
    startStream(next);
  };

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    const next = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch (e) {
      setTorchSupported(false);
      alert("Bu kamera flaş kontrolünü desteklemiyor.");
    }
  };

  const applyZoom = async (val) => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    setZoom(val);
    try {
      await track.applyConstraints({ advanced: [{ zoom: val }] });
    } catch { /* silent — kimi cihazda advanced ile hata verebilir */ }
  };

  const capture = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || !ready) return;
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 720;
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, w, h);
    c.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      setCaptured({ url, blob });
    }, jpegType, 0.9);
  };

  const retake = () => {
    if (captured?.url) URL.revokeObjectURL(captured.url);
    setCaptured(null);
  };

  const accept = () => {
    if (!captured?.blob) return;
    const fname = `label-${Date.now()}.jpg`;
    const file = new File([captured.blob], fname, { type: jpegType });
    // Close first so parent's OCR modal doesn't compete for focus
    stopStream();
    URL.revokeObjectURL(captured.url);
    setCaptured(null);
    onCapture?.(file);
    onClose?.();
  };

  return (
    <Modal open={open} onClose={() => { stopStream(); onClose?.(); }} title={title} size="lg">
      <div className="space-y-3">
        {err && (
          <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700 flex items-start gap-2" data-testid="camera-error">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0"/>
            <span>{err}</span>
          </div>
        )}
        <div className="relative bg-black rounded-md overflow-hidden aspect-[4/3] flex items-center justify-center">
          {!captured && (
            <>
              {!ready && !err && (
                <div className="absolute inset-0 flex items-center justify-center text-white/70">
                  <Loader2 size={28} className="animate-spin"/>
                </div>
              )}
              <video
                ref={videoRef}
                playsInline
                muted
                autoPlay
                className={`w-full h-full object-cover ${ready ? "opacity-100" : "opacity-0"}`}
                data-testid="camera-video"
              />
              {/* iter94 in-frame flash + zoom overlay controls */}
              {ready && (torchSupported || zoomCap) && (
                <div className="absolute right-2 top-2 flex flex-col gap-2 items-end">
                  {torchSupported && (
                    <button
                      type="button"
                      onClick={toggleTorch}
                      title={torchOn ? "Flaşı Kapat" : "Flaşı Aç"}
                      className={`p-2 rounded-full backdrop-blur ${torchOn ? "bg-amber-400/90 text-slate-900" : "bg-black/50 text-white hover:bg-black/70"}`}
                      data-testid="camera-torch"
                    >
                      {torchOn ? <Zap size={18}/> : <ZapOff size={18}/>}
                    </button>
                  )}
                </div>
              )}
              {ready && zoomCap && zoomCap.max > zoomCap.min && (
                <div className="absolute left-2 right-2 bottom-2 bg-black/50 backdrop-blur rounded-full px-3 py-1.5 flex items-center gap-3 text-white text-xs">
                  <span className="font-mono w-8">{zoom.toFixed(1)}x</span>
                  <input
                    type="range"
                    min={zoomCap.min}
                    max={zoomCap.max}
                    step={zoomCap.step || 0.1}
                    value={zoom}
                    onChange={e => applyZoom(parseFloat(e.target.value))}
                    className="flex-1 accent-emerald-400"
                    data-testid="camera-zoom"
                    aria-label="Yakınlaştırma"
                  />
                  <span className="font-mono w-8 text-right opacity-70">{zoomCap.max.toFixed(1)}x</span>
                </div>
              )}
            </>
          )}
          {captured && (
            <img src={captured.url} alt="Çekilen" className="w-full h-full object-contain" data-testid="camera-preview"/>
          )}
        </div>
        <canvas ref={canvasRef} className="hidden"/>
        <div className="flex items-center justify-center gap-2">
          {!captured ? (
            <>
              <Btn variant="secondary" onClick={switchCamera} disabled={!ready} data-testid="camera-switch">
                <RefreshCw size={14}/> Ön/Arka
              </Btn>
              <Btn onClick={capture} disabled={!ready} data-testid="camera-shoot" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Camera size={16}/> Fotoğraf Çek
              </Btn>
              <Btn variant="secondary" onClick={() => { stopStream(); onClose?.(); }} data-testid="camera-cancel">
                <X size={14}/> İptal
              </Btn>
            </>
          ) : (
            <>
              <Btn variant="secondary" onClick={retake} data-testid="camera-retake">
                <RefreshCw size={14}/> Tekrar Çek
              </Btn>
              <Btn onClick={accept} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="camera-accept">
                Kullan
              </Btn>
            </>
          )}
        </div>
        {ready && !torchSupported && !zoomCap && (
          <div className="text-[10px] text-center text-slate-400 italic">
            Bu cihazda flaş / yakınlaştırma kontrolü kullanılamıyor.
          </div>
        )}
      </div>
    </Modal>
  );
}
